// Copyright 2025, University of Maryland, All Rights Reserved

import React, { useEffect, useRef, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { Button, cn } from '@/components/ui'

export interface CopyButtonProps {
  /** Text to copy to clipboard */
  text: string
  /** Optional className for the button */
  className?: string
  /** Size variant */
  size?: 'sm' | 'default'
  /** Show label text */
  showLabel?: boolean
}

/**
 * Button that copies text to clipboard with visual feedback.
 */
export const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  className,
  size = 'sm',
  showLabel = true,
}) => {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<number>()

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleCopy = async () => {
    if (!text) return

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }

      setCopied(true)
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      console.warn('Failed to copy text to clipboard.', error)
    }
  }

  return (
    <Button
      type="button"
      size={size}
      variant="outline"
      className={cn(
        'gap-1',
        size === 'sm' && 'h-7 px-2 text-xs',
        className
      )}
      onClick={handleCopy}
    >
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
      {showLabel && (copied ? 'Copied!' : 'Copy')}
    </Button>
  )
}
